import React, { useEffect, useState, useCallback } from 'react';
import { Container, Row, Col, Spinner, Button } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { searchGeneralProducts } from '../../api/aliApi';
import AliSearchBar from './AliSearchBar';
import ProductCard from './ProductCard';
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';
import HeroSection from '../../components/HeroSection';

const AliProductList = () => {
  const [products, setProducts] = useState([]);
  const [keyword, setKeyword] = useState('auto parts');
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showTop, setShowTop] = useState(false);
  const [autoLoadDone, setAutoLoadDone] = useState(false);

  const [searchParams] = useSearchParams();

  const fetchData = useCallback(
    async (reset = false, kw = keyword, cat = category) => {
      if (loading) return;
      setLoading(true);
      try {
        const currentPage = reset ? 1 : page;
        const data = await searchGeneralProducts(kw, cat, currentPage);
        const fetched =
          data.aliexpress_ds_text_search_response?.data?.products?.selection_search_product || [];

        if (reset) {
          setProducts(fetched);
          setPage(2);
        } else {
          setProducts((prev) => [...prev, ...fetched]);
          setPage((prev) => prev + 1);
        }
        setHasMore(fetched.length > 0);
      } catch (err) {
        console.error('Search failed', err);
      } finally {
        setLoading(false);
      }
    },
    [keyword, category, page, loading]
  );

  useEffect(() => {
    const q = searchParams.get('search') || 'auto parts';
    const cat = searchParams.get('category') || '';
    setKeyword(q);
    setCategory(cat);
    setPage(1);
    setHasMore(true);
    setAutoLoadDone(false);
    fetchData(true, q, cat);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (!autoLoadDone && products.length > 0 && page === 2 && hasMore && !loading) {
      fetchData();
      setAutoLoadDone(true);
    }
  }, [products, page, loading, hasMore, autoLoadDone, fetchData]);

  useEffect(() => {
    const onScroll = () => {
      if (
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 300 &&
        hasMore &&
        !loading
      ) {
        fetchData();
      }
      setShowTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [fetchData, hasMore, loading]);

  const handleSearch = async ({ keyword: kw, category: cat }) => {
    const k = kw || 'auto parts';
    const c = cat || '';
    setKeyword(k);
    setCategory(c);
    setPage(1);
    setHasMore(true);
    setAutoLoadDone(false);
    await fetchData(true, k, c);
  };

  const handleBackToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <>
      <Navbar />
      <HeroSection />

      <Container className="py-4">
        {/* Primary blur header strip */}
        <div
          className="rounded-4 p-3 mb-3"
          style={{
            background: 'rgba(13,110,253,.06)',
            border: '1px solid rgba(13,110,253,.15)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)'
          }}
        >
          <Row className="align-items-center g-2">
            <Col>
              <h4 className="mb-0 fw-bold text-primary">
                AliExpress Auto Parts{' '}
                {keyword ? (
                  <span className="text-muted fw-normal">– Results for “{keyword}”</span>
                ) : null}
              </h4>
            </Col>
          </Row>
        </div>

        <AliSearchBar onSearch={handleSearch} />

        <Row className="g-3">
          {products.map((p) => (
            <Col key={p.itemId} xs={6} sm={6} md={4} lg={3}>
              <ProductCard product={p} />
            </Col>
          ))}
        </Row>

        {loading && (
          <div className="text-center my-4">
            <Spinner animation="border" variant="primary" />
          </div>
        )}

        {!loading && hasMore && (
          <div className="text-center mt-3">
            <Button onClick={() => fetchData()} variant="primary">
              Load More
            </Button>
          </div>
        )}

        {!hasMore && !loading && (
          <p className="text-muted text-center mt-3">No more products to load.</p>
        )}

        {showTop && (
          <div style={{ position: 'fixed', bottom: 30, right: 30, zIndex: 999 }}>
            <Button variant="primary" onClick={handleBackToTop}>
              ↑ Back to Top
            </Button>
          </div>
        )}
      </Container>

      <Footer />
    </>
  );
};

export default AliProductList;
